import React, { FC } from 'react';
import { HeaderTextCtaProps, HeaderTextCtaModel } from './HeaderTextCta.types';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { StyledHeaderTextCta } from './HeaderTextCta.styles';
import { Button, LogoContainer } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { SMALL_FONT_SIZE } from '../../modules/constants/constant';
import { useDCADynamicModelAlloy } from '../../modules/helpers';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

export const HeaderTextCta: FC<HeaderTextCtaProps> = (props: HeaderTextCtaProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: HeaderTextCtaModel = targetData ? targetData : props?.model;
  const isLogoFlagEnabled = props.featureFlagLogo?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION];

  return (
    <StyledHeaderTextCta className={componentValues?.styleclass} id={componentValues?.anchorId}>
      <div className={clsx('header-text container', !componentValues?.headerText && 'd-none')}>
        <div
          className={clsx(
            'row',
            !componentValues?.bodyText && !componentValues?.ctaProperties?.linkUrl
              ? !componentValues?.eyebrowText && componentValues?.headerFontSize === SMALL_FONT_SIZE
                ? 'only-header-styles'
                : 'header-eyebrow-styles'
              : 'header-text-cta-styles'
          )}
        >
          {componentValues && componentValues?.alignment === 'center' && (
            <>
              {componentValues?.logo && (
                <div className={clsx(isLogoFlagEnabled && 'p-0', 'header-text-cta-logo-container text-center')}>
                  {!isLogoFlagEnabled ? (
                    <span className={clsx(componentValues?.logo, 'header-text-logo')}></span>
                  ) : (
                    <LogoContainer
                      brand={componentValues?.logo || ''}
                      color={'background-aware'}
                      aspectRatio={'3:2'}
                      width={150}
                      height={60}
                      alignment={'Centered'}
                      isLink={false}
                    />
                  )}
                </div>
              )}
              {componentValues.eyebrowText && (
                <div
                  className={clsx(
                    isLogoFlagEnabled ? 'pb-0' : 'header-text-eyebrow',
                    'col-md-6 col-12 offset-md-3 mx-auto text-center t-title-alt-s'
                  )}
                >
                  {componentValues.eyebrowText}
                </div>
              )}
              {componentValues?.headerPrimaryHOneTag && (
                <h1
                  className={clsx(
                    'header-text-heading col-md-8 offset-md-2 mx-auto my-0 py-3 text-center col-12',
                    componentValues?.headerFontSize === SMALL_FONT_SIZE ? 't-title-s py-lg-4' : 't-title-l'
                  )}
                >
                  {componentValues?.headerText}
                </h1>
              )}
              {!componentValues?.headerPrimaryHOneTag && (
                <div
                  className={clsx(
                    'header-text-heading col-md-8 offset-md-2 mx-auto my-0 py-3 text-center col-12',
                    componentValues?.headerFontSize === SMALL_FONT_SIZE ? 't-title-s py-lg-4' : 't-title-l'
                  )}
                >
                  {componentValues?.headerText}
                </div>
              )}
              <div className="col-12 text-center">
                {componentValues?.bodyText && (
                  <div
                    className="p-0 mx-auto t-font-alt-m body-text"
                    dangerouslySetInnerHTML={{ __html: componentValues?.bodyText }}
                  ></div>
                )}

                {componentValues?.ctaProperties?.linkUrl && (
                  <div className="d-flex justify-content-center mt-4">
                    {componentValues?.enableTracking && componentValues?.ctaProperties?.linkText && (
                      <Button
                        isLink={true}
                        className={clsx(
                          componentValues?.ctaProperties?.ctaType === 'tertiaryLink'
                            ? componentValues?.ctaProperties?.openInANewTab
                              ? 'm-link-tertiary-button-external'
                              : 'm-link-tertiary-button'
                            : componentValues?.ctaProperties?.ctaType === 'primaryButton'
                            ? 'm-button-primary m-button-l px-4'
                            : 'm-button-secondary m-button-l px-4'
                        )}
                        href={componentValues?.ctaProperties?.linkUrl}
                        custom_click_track_value={componentValues?.clickTrackValue}
                        target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
                        rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
                        ariaLabel={componentValues?.ctaProperties?.linkText}
                      >
                        {componentValues?.ctaProperties?.linkText}
                      </Button>
                    )}
                    {!componentValues?.enableTracking && componentValues?.ctaProperties?.linkText && (
                      <Button
                        isLink={true}
                        className={clsx(
                          componentValues?.ctaProperties?.ctaType === 'tertiaryLink'
                            ? componentValues?.ctaProperties?.openInANewTab
                              ? 'm-link-tertiary-button-external'
                              : 'm-link-tertiary-button'
                            : componentValues?.ctaProperties?.ctaType === 'primaryButton'
                            ? 'm-button-primary m-button-l px-4'
                            : 'm-button-secondary m-button-l px-4'
                        )}
                        href={componentValues?.ctaProperties?.linkUrl}
                        target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
                        rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
                        ariaLabel={componentValues?.ctaProperties?.linkText}
                      >
                        {componentValues?.ctaProperties?.linkText}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {componentValues && componentValues?.alignment === 'none' && (
            <>
              <div className="col-xl-8 col-md-6 col-12 pr-3 ar-styles">
                {componentValues?.eyebrowText && (
                  <div className="p-0 col-md-10 col-10 pt-0 t-title-alt-s">{componentValues?.eyebrowText}</div>
                )}
                {componentValues?.headerPrimaryHOneTag && (
                  <h1
                    className={clsx(
                      'header-text-heading p-0 col-md-12 mb-md-0 col-12 t-title-l mb-3',
                      componentValues?.eyebrowText && 'mt-3',
                      componentValues?.headerFontSize === SMALL_FONT_SIZE ? 't-title-s' : 't-title-l'
                    )}
                  >
                    {componentValues?.headerText}
                  </h1>
                )}
                {!componentValues?.headerPrimaryHOneTag && (
                  <div
                    className={clsx(
                      'header-text-heading p-0 col-md-12 mb-md-0 col-12 t-title-l mb-3',
                      componentValues?.eyebrowText && 'mt-3',
                      componentValues?.headerFontSize === SMALL_FONT_SIZE ? 't-title-s' : 't-title-l'
                    )}
                  >
                    {componentValues?.headerText}
                  </div>
                )}
              </div>
              <div className="col-12 col-xl-4 col-md-6 pl-2 pr-0">
                {componentValues?.bodyText && (
                  <div
                    className="p-0 col-md-12 col-12 t-font-alt-m"
                    dangerouslySetInnerHTML={{ __html: componentValues?.bodyText }}
                  ></div>
                )}

                {componentValues?.ctaProperties?.linkUrl && (
                  <div className={clsx('d-flex', componentValues?.bodyText && 'mt-4')}>
                    {componentValues.enableTracking && componentValues?.ctaProperties?.linkText && (
                      <Button
                        isLink={true}
                        className={clsx(
                          componentValues?.ctaProperties?.ctaType === 'tertiaryLink'
                            ? componentValues?.ctaProperties?.openInANewTab
                              ? 'm-link-tertiary-button-external'
                              : 'm-link-tertiary-button'
                            : componentValues?.ctaProperties?.ctaType === 'primaryButton'
                            ? 'm-button-primary m-button-l px-4'
                            : 'm-button-secondary m-button-l px-4'
                        )}
                        href={componentValues?.ctaProperties?.linkUrl}
                        custom_click_track_value={componentValues?.clickTrackValue}
                        target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
                        rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
                        ariaLabel={componentValues?.ctaProperties?.linkText}
                      >
                        {componentValues?.ctaProperties?.linkText}
                      </Button>
                    )}
                    {!componentValues?.enableTracking && componentValues?.ctaProperties?.linkText && (
                      <Button
                        isLink={true}
                        className={clsx(
                          componentValues?.ctaProperties?.ctaType === 'tertiaryLink'
                            ? componentValues?.ctaProperties?.openInANewTab
                              ? 'm-link-tertiary-button-external'
                              : 'm-link-tertiary-button'
                            : componentValues?.ctaProperties?.ctaType === 'primaryButton'
                            ? 'm-button-primary m-button-l px-4'
                            : 'm-button-secondary m-button-l px-4'
                        )}
                        href={componentValues?.ctaProperties?.linkUrl}
                        target={componentValues?.ctaProperties?.openInANewTab ? '_blank' : ''}
                        rel={componentValues?.ctaProperties?.openInANewTab ? 'noopener noreferrer' : ''}
                        ariaLabel={componentValues?.ctaProperties?.linkText}
                      >
                        {componentValues?.ctaProperties?.linkText}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </StyledHeaderTextCta>
  );
};

export const HeaderTextCtaConfig = {
  emptyLabel: 'LuxuryHeaderTextCta',
  isEmpty: () => true,
  resourceType: `mi-aem-luxury-spa/components/content/header-text-cta/v1/header-text-cta`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HeaderTextCtaEditable = (props: any) => (
  <div data-testid="luxury-HeaderTextCta" data-component-name="m-luxury-HeaderTextCta">
    <EditableComponent config={HeaderTextCtaConfig} {...props}>
      <HeaderTextCta {...props} />
    </EditableComponent>
  </div>
);
